/* eslint-env browser */
import React from 'react';
import classnames from 'classnames';
import ReactDOM from 'react-dom';
import { Manager, Reference, Popper } from 'react-popper';
import createInteractions from './popover-popper.interactions';
import { propTypes, defaultProps } from './common';
import PopoverContentContainer from './popover-content-container/popover-content-container';
import { addPopoverPortalLayer } from './utils';

const popoverLayer = addPopoverPortalLayer();

class PopoverPopper extends React.Component {
  static propTypes = propTypes;
  static defaultProps = defaultProps;

  constructor(props) {
    super(props);
    this.state = {
      open: false,
      closing: false,
    };
    this.interactions = createInteractions(
      this.checkHoverShow,
      this.checkClickShow,
      this.checkTriggerHide,
      this.checkCloseTrigger
    );
    if (this.props.open) {
      this.interactions.addOutsideListeners();
    }
  }

  componentWillUnmount() {
    this.removeCloseTriggerListener();
    this.clearHideTimeouts();
  }

  getEventTargetIsWithinTarget = (event) => {
    if (!this.targetRef) {
      return false;
    }
    return this.targetRef.contains(event.target);
  };

  getEventTargetIsWithinPopover = (event) => {
    if (!this.popperContentDiv) {
      return false;
    }
    return this.popperContentDiv.contains(event.target);
  };

  getModifiers = () => {
    const { constraint, constraintPadding } = this.props;
    return {
      preventOverflow: {
        boundariesElement: constraint,
        padding: constraintPadding,
      },
    };
  };

  checkClickShow = () => {
    if (!this.props.showOnHover) {
      this.open();
    }
  };

  checkHoverShow = () => {
    if (this.props.showOnHover) {
      this.open();
    }
  };

  checkTriggerHide = () => {
    if (this.props.showOnHover) {
      this.triggerHide();
    }
  };

  triggerHide = () => {
    this.hideTimeoutId = window.setTimeout(this.hide, this.props.hideDelay);
  };

  hide = () => {
    if (this.state.closing || this.props.open === false) {
      return;
    }
    this.setState({
      closing: true,
    });
    this.closingTimeoutId = setTimeout(
      this.close,
      this.props.transitionTimeout
    );
  };

  close = () => {
    this.props.onVisibilityChange(false);
    this.removeCloseTriggerListener();
    this.setState({
      open: false,
      closing: true,
    });
  };

  open = () => {
    this.props.onVisibilityChange(true);
    this.interactions.addOutsideListeners();
    this.clearHideTimeouts();
    this.setState({
      open: true,
      closing: false,
    });
  };

  checkCloseTrigger = (event) => {
    if (
      event.target === window ||
      (!this.getEventTargetIsWithinPopover(event) &&
        !this.getEventTargetIsWithinTarget(event))
    ) {
      this.outerActionTimeoutId = window.setTimeout(
        this.onOuterAction,
        this.props.hideDelay
      );
      this.triggerHide();
    }
  };

  handlePopperContentRef = (el) => {
    this.popperContentDiv = el;
  };

  handleTargetRef = (el) => {
    this.targetRef = el;
  };

  clearHideTimeouts() {
    clearTimeout(this.outerActionTimeoutId);
    clearTimeout(this.closingTimeoutId);
    clearTimeout(this.hideTimeoutId);
  }

  removeCloseTriggerListener = () => {
    this.interactions.removeOutsideListeners();
  };

  render() {
    const {
      className,
      children,
      content,
      orientation,
      themeType,
      id,
      disabled,
      ...rest
    } = this.props;
    const { open = this.state.open } = this.props;
    const { closing } = this.state;
    const targetWrapperInteractions = !disabled
      ? this.interactions.targetWrapper
      : null;
    const classes = classnames('popover-popper', className);
    return (
      <div id={id} className={classes}>
        <Manager>
          <Reference>
            {({ ref }) => (
              <div
                {...targetWrapperInteractions}
                className="target-wrapper"
                ref={ref}
                tabIndex={0}
              >
                <div ref={this.handleTargetRef}>{children}</div>
              </div>
            )}
          </Reference>
          {open &&
            ReactDOM.createPortal(
              <Popper modifiers={this.getModifiers()} placement={orientation}>
                {({ ref, style, placement }) => (
                  <div ref={ref} style={style} data-placement={placement}>
                    <div ref={this.handlePopperContentRef}>
                      <PopoverContentContainer
                        onHide={this.triggerHide}
                        orientation={placement}
                        closing={closing}
                        themeType={themeType}
                        {...rest}
                      >
                        {content}
                      </PopoverContentContainer>
                    </div>
                  </div>
                )}
              </Popper>,
              popoverLayer
            )}
        </Manager>
      </div>
    );
  }
}

export default PopoverPopper;
